import { action, computed, observable, makeObservable } from 'mobx';
import { RemoteResourceV2, updateRemoteResourceV2 } from '../../../../commonv2/api/RemoteResourceV2';
import { EventDetails, EventId, EventChargeScope, EventStatus, UserEventReservationStatus } from '../../../types/event';
import { EventApis } from '../../../api/event';
import { UserEventReservationApis } from '../../../api/user';
import { CurrentUserState } from '../../../states/CurrentUserState';
import { TokenType } from '../../../../commonv3/types/tokens';
import { EventStatusUtils } from '../utils/EventStatusUtils';
import moment from 'moment-timezone';
import { EventWaitlistState } from '../../../../mainv3/components/organisms/events/states/EventWaitlistState';

export class DetailsPageState {
  eventId!: EventId;
  event: RemoteResourceV2<EventDetails> = RemoteResourceV2.loading();
  alreadyReserved = false;
  hasAffordableTokenForNow = false;
  hasAffordableTokenForEventStartAt = false;
  eventWaitlistState = new EventWaitlistState();
  initialized = false;

  constructor() {
    makeObservable(this, {
      eventId: observable,
      event: observable,
      alreadyReserved: observable,
      hasAffordableTokenForNow: observable,
      hasAffordableTokenForEventStartAt: observable,
      eventWaitlistState: observable,
      initialized: observable,
      appliedPaymentType: computed,
      eventStatus: computed,
      userEventReservationStatus: computed,
      membershipAvailable: computed,
      isIWS: computed,
      init: action,
    });
  }

  get appliedPaymentType() {
    if (this.event.isEmpty() || this.event.get().charge_scope === EventChargeScope.ALL_USERS) {
      return 'credit_card';
    }
    return this.membershipAvailable ? 'token' : 'credit_card';
  }

  get eventStatus(): EventStatus | null {
    if (this.event.isEmpty()) return null;
    return EventStatusUtils.getEventStatus(this.event.get());
  }

  get userEventReservationStatus(): UserEventReservationStatus | null {
    if (this.event.isEmpty()) return null;
    const event = this.event.get();
    return {
      eventId: event.id,
      reserved: this.alreadyReserved,
      hasAffordableTokenForNow: this.hasAffordableTokenForNow,
      hasAffordableTokenForEventStartAt: this.hasAffordableTokenForEventStartAt,
    };
  }

  get membershipAvailable(): boolean {
    return this.hasAffordableTokenForEventStartAt;
  }

  get isIWS(): boolean {
    // INNOVATIVE WOMEN'S SUMMIT のみ会社名と職種を必須にする
    // https://sheinc.atlassian.net/browse/SHE-5087
    // INNOVATIVE WOMEN'S SUMMIT のみ個別の利用規約へ同意を求める
    // https://sheinc.atlassian.net/browse/SHE-5084
    // INNOVATIVE WOMEN'S SUMMIT のみ応募者数の表示をフラグ制御にする
    // https://sheinc.atlassian.net/browse/SHE-5091
    if (['1581', '1573'].find((id) => this.eventId === Number(id))) return true;
    return false;
  }

  async init(eventId: EventId, userState: CurrentUserState) {
    this.eventId = eventId;
    const [event] = await Promise.all([
      updateRemoteResourceV2(
        this.event,
        (v) => (this.event = v),
        () => EventApis.find(this.eventId)
      ),
      this.eventWaitlistState.init(),
      userState.reloadUser(),
    ]);

    if (userState.isSignedIn()) {
      const userReservations = await UserEventReservationApis.findAll();
      this.alreadyReserved =
        userReservations.find((r) => r.event_type === 'Event' && r.event_id === this.eventId) !== undefined;
      if (event) {
        this.hasAffordableTokenForNow =
          userState.availableTokens.findAffordableToken(TokenType.EVENT, 1, moment()) != null;
        this.hasAffordableTokenForEventStartAt =
          userState.availableTokens.findAffordableToken(TokenType.EVENT, 1, event.starts_at) != null;
      }
    }
    this.initialized = true;
  }
}
